import React, { useEffect, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useTheme } from '../../hooks/useTheme';
import { BookOpen, FileText, Video, ExternalLink, Download } from 'lucide-react';
import { FaLinkedin, FaReddit } from 'react-icons/fa';
import api from '../../api/api';
import { extractVideoId } from './TutorialsSupport';

/**
 * Getting Started — help content managed from the admin panel (Lookup
 * Management -> Resources, placement = 'getting-started'). Each resource is a
 * link (opens in a new tab) or an uploaded file (downloaded via the API). The
 * community links are real and match the ones on the Contact page.
 */

const COMMUNITY_LINKS = [
    { name: 'LinkedIn Community', href: 'https://www.linkedin.com/company/buildmybizbrand/', icon: FaLinkedin, colorClasses: { light: 'bg-blue-100 text-blue-600', dark: 'bg-blue-900/50 text-blue-400' } },
    { name: 'Reddit Community', href: 'https://www.reddit.com/r/BuildMyBizBrand/', icon: FaReddit, colorClasses: { light: 'bg-orange-100 text-orange-600', dark: 'bg-orange-900/50 text-orange-400' } },
];

const fileHref = (id) => `${api.defaults.baseURL || ''}/resources/${id}/file`;
const typeIcon = (type) => (type === 'video' ? Video : type === 'guide' ? BookOpen : FileText);

export default function GettingStarted() {
    const { isDarkMode } = useTheme();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await api.get('/resources', { params: { placement: 'getting-started' } });
                if (alive) setResources(Array.isArray(res.data?.items) ? res.data.items : []);
            } catch {
                if (alive) setResources([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const pageBg = `min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`;
    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';
    const card = isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-200 hover:border-slate-300';
    const iconBox = isDarkMode ? 'bg-primary-900/30 border border-primary-800 text-primary-400' : 'bg-primary-50 border border-primary-200 text-primary-600';
    const skBase = isDarkMode ? '#334155' : '#e2e8f0';
    const skHi = isDarkMode ? '#475569' : '#f1f5f9';

    const ResourceCard = ({ r }) => {
        const isFile = r.source === 'file';
        //const href = isFile ? fileHref(r.id) : r.url;
        // A YouTube resource can be a bare video id (that became a dead relative
        // link): link to the video itself, as Tutorials does
        const vid = r.source === 'youtube' ? extractVideoId(r.url) : '';
        const href = isFile ? fileHref(r.id) : vid ? `https://www.youtube.com/watch?v=${vid}` : r.url;
        const Icon = typeIcon(r.type);
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-3 rounded-2xl border p-4 transition-colors ${card}`}>
                <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBox}`}>
                    <Icon size={19} />
                </span>
                <div className="min-w-0">
                    <p className={`font-semibold truncate ${heading}`}>{r.title}</p>
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}>
                        {isFile ? <><Download size={13} /> Download</> : <><ExternalLink size={13} /> Open</>}
                    </span>
                </div>
            </a>
        );
    };

    return (
        <div className={pageBg}>
            <div className="max-w-3xl mx-auto px-4 py-8">
                <h1 className={`text-3xl font-extrabold ${heading}`}>Getting Started</h1>
                <p className={`mt-2 ${sub}`}>New to Pono Pilot? Guides and resources to help you get up and running.</p>

                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {loading ? (
                        <SkeletonTheme baseColor={skBase} highlightColor={skHi}>
                            {[0, 1].map((i) => (
                                <div key={i} className={`rounded-2xl border p-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                    <Skeleton height={44} borderRadius={12} />
                                </div>
                            ))}
                        </SkeletonTheme>
                    ) : resources.length === 0 ? (
                        <div className={`sm:col-span-2 rounded-2xl border-2 border-dashed p-12 text-center ${isDarkMode ? 'border-slate-700 bg-slate-800/50' : 'border-slate-300 bg-white'}`}>
                            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${iconBox}`}>
                                <BookOpen size={28} />
                            </div>
                            <h2 className={`text-xl font-bold ${heading}`}>Coming soon</h2>
                        </div>
                    ) : (
                        resources.map((r) => <ResourceCard key={r.id} r={r} />)
                    )}
                </div>

                <h3 className={`mt-9 mb-3 text-xs font-bold uppercase tracking-wider ${sub}`}>Community</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {COMMUNITY_LINKS.map(({ name, href, icon: Icon, colorClasses }) => (
                        <a key={name} href={href} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-3 rounded-2xl border p-4 transition-colors ${card}`}>
                            <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isDarkMode ? colorClasses.dark : colorClasses.light}`}>
                                <Icon size={19} />
                            </span>
                            <span className={`font-semibold ${heading}`}>{name}</span>
                        </a>
                    ))}
                </div>
            </div>
        </div>
    );
}
